import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["container", "input", "increase", "decrease", "hint"];

  static values = {
    min: Number,
    max: Number,
    step: Number,
    leadingZero: Boolean
  }

  connect() {
    this._boundOnBlur = this._onBlur.bind(this);
    this._boundOnChange = this._onChange.bind(this);
    this._boundEnable = this._enable.bind(this);
    this._boundDisable = this._disable.bind(this);
    this._boundUpdateHint = this._updateHint.bind(this);

    this.inputTarget.addEventListener('blur', this._boundOnBlur);
    this.inputTarget.addEventListener('change', this._boundOnChange);
    this.inputTarget.addEventListener('promote:number-picker-enable', this._boundEnable);
    this.inputTarget.addEventListener('promote:number-picker-disable', this._boundDisable);
    this.inputTarget.addEventListener('promote:number-picker-update-hint', this._boundUpdateHint);
  }

  disconnect() {
    this.inputTarget.removeEventListener('blur', this._boundOnBlur);
    this.inputTarget.removeEventListener('change', this._boundOnChange);
    this.inputTarget.removeEventListener('promote:number-picker-enable', this._boundEnable);
    this.inputTarget.removeEventListener('promote:number-picker-disable', this._boundDisable);
    this.inputTarget.removeEventListener('promote:number-picker-update-hint', this._boundUpdateHint);
  }

  onKeyDown(event) {
    const key = event.key;
    const allowedKeys = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Enter',
      'Tab',
      'Delete',
      'Backspace'
    ];

    if (!allowedKeys.includes(key) && (isNaN(parseInt(key)) || event.shiftKey)) {
      event.preventDefault();
    }

    let currentValue = this.inputTarget.value;

    if (key === 'ArrowUp') {
      this.increase();
    } else if (key === 'ArrowDown') {
      this.decrease();
    } else if (key === 'Enter') {
      this.inputTarget.blur();
    }
  }

  preventBlur(event) {
    event.preventDefault();
  }

  increase() {
    if (this.inputTarget.disabled) return;

    if (this.inputTarget.value === '') {
      this._setInitialValue();
    } else {
      this._increaseInputValue();
    }

    this.inputTarget.focus();
    this._dispatchBlurEvent();
  }

  decrease() {
    if (this.inputTarget.disabled) return;

    if (this.inputTarget.value === '') {
      this._setInitialValue();
    } else {
      this._decreaseInputValue();
    }

    this.inputTarget.focus();
    this._dispatchBlurEvent();
  }

  // This method can be used when calling a custom change event
  // Example of custom event dispatch:
  // <inputTarget>.dispatchEvent(new CustomEvent('change', { detail: { value: <value> } }));
  _onChange(event) {
    let value;

    if(!event.detail) {
      value = event.target.value;
    } else {
      value = event.detail.value;
    }

    if (value === '') {
      this._clearValue();
    } else if (value === 0) {
      this._setInitialValue();
    } else {
      this._setInputTargetValue(value);
    }
  }

  _onBlur() {
    let value = this.inputTarget.value;

    if (value === '') {
      return;
    }

    this._setInputTargetValue(value);
  }

  _setInitialValue () {
    let value;

    if (this.minValue) {
      value = this.minValue;
    } else {
      value = 0;
    }

    this.inputTarget.value = this._addLeadingZero(value);
  }

  _setInputTargetValue(value) {
    let targetValue;

    if (this.maxValue && value > this.maxValue) {
      targetValue = this.maxValue;
    } else if (this.minValue && value < this.minValue) {
      targetValue = this.minValue;
    } else {
      targetValue = parseInt(value);
    }

    this.inputTarget.value = this._addLeadingZero(targetValue);
  }

  _clearValue() {
    this.inputTarget.value = '';
  }

  _increaseInputValue() {
    let value = parseInt(this.inputTarget.value);

    if (this.stepValue) {
      value = this._closestStepValueAbove(value);
    } else {
      value++;
    }

    if (this.maxValue && value > this.maxValue) {
      return;
    }

    this.inputTarget.value = this._addLeadingZero(value);
    this._dispatchChangeEvent();
  }

  _decreaseInputValue() {
    let value = parseInt(this.inputTarget.value);

    if (this.stepValue) {
      value = this._closestStepValueBelow(value);
    } else {
      value--;
    }

    if ((this.minValue && value < this.minValue) || value < 0) {
      return;
    }

    this.inputTarget.value = this._addLeadingZero(value);
    this._dispatchChangeEvent();
  }

  _addLeadingZero(value) {
    if (!this.leadingZeroValue) {
      return value;
    }

    return value < 10 ? `0${value}` : value;
  }

  _closestStepValueAbove(value) {
    let threshold = this._thresholdValue(value);

    if (value < threshold) {
      return threshold;
    } else {
      return threshold + this.stepValue;
    }
  }

  _closestStepValueBelow(value) {
    let threshold = this._thresholdValue(value);

    if (value > threshold) {
      return threshold;
    } else {
      return threshold - this.stepValue;
    }
  }

  _thresholdValue(value) {
    let initialValue = this.minValue || 0;

    return Math.floor(value/this.stepValue) * this.stepValue + initialValue;
  }

  _dispatchBlurEvent() {
    this.inputTarget.dispatchEvent(new Event('blur'));
  }

  _dispatchChangeEvent() {
    this.inputTarget.dispatchEvent(new Event('change'));
  }

  _enable() {
    this.inputTarget.disabled = '';
    this.containerTarget.classList.remove('disabled');
    this.containerTarget.classList.add('cursor-pointer');
  }

  _disable() {
    this.inputTarget.disabled = 'disabled';
    this.containerTarget.classList.add('disabled');
    this.containerTarget.classList.remove('pointer-events-none');
  }

  _updateHint(event) {
    const value = event.detail.value;
    this.hintTarget.querySelector('.js-inputs-hint-component-text').innerHTML = value;
    this.hintTarget.classList.toggle('hidden', !value);
  }
}
